import type { GameStateManager } from '../game/GameState';
import type { GameData, ContainmentCompletionResult, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';
import { getAnomalyDisplayName } from '../utils/AnomalyNameResolver';

export class BreachContainmentModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: ContainmentCompletionResult): void {
    this.render(result);
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    // Play major event sound only for successful containment
    if (!result.effects.immediateReEscape) {
      AudioManager.getInstance().playMajorEvent();
    } else {
      // Play effect sound for partial containment (re-escape)
      AudioManager.getInstance().playEffect();
    }
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    this.gameState.dequeueContainmentCompletion();
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(result: ContainmentCompletionResult): void {
    const strings = this.gameData.uiStrings;
    const researcher = this.gameState.getResearcher(result.researcherId);
    const researcherName = researcher ? researcher.data.name : 'Unknown Researcher';

    // Try to find the anomaly to get resolved name
    let displayName = result.anomalyName;
    const state = this.gameState.getState();
    
    // Check normal anomalies
    for (const anomaly of state.anomalies) {
      if (anomaly.data.name === result.anomalyName) {
        displayName = getAnomalyDisplayName(anomaly, this.gameData);
        break;
      }
    }
    
    // Check breached anomalies if not found
    if (displayName === result.anomalyName) {
      for (const breached of state.breachedAnomalies) {
        if (breached.originalAnomaly.data.name === result.anomalyName) {
          displayName = getAnomalyDisplayName(breached.originalAnomaly, this.gameData);
          break;
        }
      }
    }

    // Build effects list with colors from data
    const effectsList: string[] = [];
    const propertyColors = strings.propertyColors || {};

    if (result.effects.lethalKilled) {
      const lethalColor = propertyColors['lethal'] || '#ff4444';
      const lethalSymbol = strings.propertySymbols?.['lethal'] || '☠';
      const lethalName = strings.properties?.['lethal'] || 'Lethal';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${lethalColor}; color: ${lethalColor}"><span class="property-symbol" style="color: ${lethalColor}; font-size: 1.5em;">${lethalSymbol}</span> <span class="property-name" style="color: ${lethalColor};">${lethalName}</span> - Researcher ${researcherName} was killed</div>`);
    } else if (result.effects.lethalBlocked) {
      effectsList.push(`<div class="effect-item" style="border-left-color: #44ff44; color: #66ff66"><span style="font-size: 1.5em;">✓</span> Lethal attack blocked by Silver Bullet</div>`);
    }

    if (result.effects.memeticSanityLoss > 0) {
      const memeticColor = propertyColors['memetic'] || '#ffaa44';
      const memeticSymbol = strings.propertySymbols?.['memetic'] || 'Ψ';
      const memeticName = strings.properties?.['memetic'] || 'Memetic';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${memeticColor}; color: ${memeticColor}"><span class="property-symbol" style="color: ${memeticColor}; font-size: 1.5em;">${memeticSymbol}</span> <span class="property-name" style="color: ${memeticColor};">${memeticName}</span> - Lost ${result.effects.memeticSanityLoss} Sanity</div>`);
    }

    if (result.effects.hyperrealAnchoringReduction > 0) {
      const hyperrealColor = propertyColors['hyperreal'] || '#ff44ff';
      const hyperrealSymbol = strings.propertySymbols?.['hyperreal'] || '✦';
      const hyperrealName = strings.properties?.['hyperreal'] || 'Hyperreal';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${hyperrealColor}; color: ${hyperrealColor}"><span class="property-symbol" style="color: ${hyperrealColor}; font-size: 1.5em;">${hyperrealSymbol}</span> <span class="property-name" style="color: ${hyperrealColor};">${hyperrealName}</span> - Anchoring reduced by ${result.effects.hyperrealAnchoringReduction}</div>`);
    }

    if (result.effects.immediateReEscape) {
      const breachingColor = propertyColors['breaching'] || '#ffaa44';
      const breachingSymbol = strings.propertySymbols?.['breaching'] || '⚡';
      const breachingName = strings.properties?.['breaching'] || 'Breaching';
      // Check if it's 3 Breaching by counting symbols in the anomaly's properties
      const state = this.gameState.getState();
      let breachingCount = 0;
      for (const breached of state.breachedAnomalies) {
        if (breached.originalAnomaly.data.name === result.anomalyName) {
          breachingCount = breached.originalAnomaly.knownProperties.filter(p => p === 'Breaching').length;
          break;
        }
      }
      if (breachingCount >= 3) {
        effectsList.push(`<div class="effect-item" style="border-left-color: ${breachingColor}; color: ${breachingColor}"><span class="property-symbol" style="color: ${breachingColor}; font-size: 1.5em;">${breachingSymbol}${breachingSymbol}${breachingSymbol}</span> <span class="property-name" style="color: ${breachingColor};">${breachingName}</span> - Anomaly has 3 Breaching properties, always escapes to a new location. Eternal cat and mouse - only letting the escape timer expire will stop it.</div>`);
      } else {
        effectsList.push(`<div class="effect-item" style="border-left-color: ${breachingColor}; color: ${breachingColor}"><span class="property-symbol" style="color: ${breachingColor}; font-size: 1.5em;">${breachingSymbol}${breachingSymbol}</span> <span class="property-name" style="color: ${breachingColor};">${breachingName}</span> - Anomaly immediately escaped again (2 Breaching properties)</div>`);
      }
    }

    let statusMessage = '';
    if (result.effects.immediateReEscape) {
      statusMessage = '<div class="containment-status partial">Containment Attempted - Anomaly Re-escaped</div>';
    } else {
      statusMessage = '<div class="containment-status success">Containment Successful</div>';
    }

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content breach-containment-modal" tabindex="0">
        <div class="modal-header">
          <h2>Containment Complete</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          <div class="containment-completion-info">
            <div class="completion-section">
              <h3>Anomaly</h3>
              <div>${displayName}</div>
            </div>
            
            <div class="completion-section">
              <h3>Researcher</h3>
              <div>${researcherName}</div>
            </div>
            
            ${statusMessage}
            
            ${effectsList.length > 0 ? `
            <div class="completion-section">
              <h3>Effects</h3>
              <div class="effects-list">
                ${effectsList.join('')}
              </div>
            </div>
            ` : ''}
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Close</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }
  }
}
